import React, { useState, useCallback, useEffect } from 'react'
import PropTypes from 'prop-types'

import { useDispatch } from 'react-redux'
import { useSelector, useMakeSelector } from 'hooks/redux'
import { isNil, propOr, prop } from 'ramda'
import strings from 'utils/strings'
import {
  QUERY_INPUT_TYPES,
  QUERY_BOX_TYPES,
  SIMPLE,
  SWITCH_INPUT,
  DIGITAL_INPUT,
} from 'utils/constants'
import { isEmptyOrNil } from 'utils/functional'
import { isNativeOutput, isCanOutput, isPinOutput } from 'utils/types/features'

import { setOutputField } from 'actions/feature/outputs'
import { switchInputType } from 'actions/feature/inputs'
import { makeSignalSelector } from 'selectors/feature/inputs'
import {
  addSingleCondition,
  deleteSingleCondition,
} from 'actions/feature/conditions'
import {
  getSingleConditionMetaData,
  getShowOutputEvaluation,
  getOutputQuery,
} from 'selectors/feature/outputs'
import { makeConditionSelector } from 'selectors/feature/conditions'

import { DEFAULT_NUM_BITS } from 'constants/conditions'

import {
  DrawerDivider,
  HoverableInfoIcon,
  InputLabel,
} from 'components/patterns'
import InputTypeEditor from 'components/feature/NavigatorPanes/editors/base/InputTypeEditor'
import Evaluation from 'components/feature/NavigatorPanes/editors/base/Evaluation'

import OutputControlSelection from './OutputControlSelection'
import { StyledInputSection } from './OutputControlSection.style'

const { CARRY, CAN_INPUT } = QUERY_INPUT_TYPES

const getDefaultNumberOfBits = ({
  isSixteenBitCan,
  isNativeOutputType,
  isSingleConditionOutput,
  isOutputBoolean,
}) => {
  if (isSixteenBitCan) return DEFAULT_NUM_BITS.CONDITION_16BIT_OUTPUT
  if (isNativeOutputType && !isOutputBoolean)
    return DEFAULT_NUM_BITS.NATIVE_OUTPUT_CAN_INPUT
  if (isSingleConditionOutput) return DEFAULT_NUM_BITS.SINGLE_CONDITION

  return DEFAULT_NUM_BITS.SUB_CONDITION
}

const OutputControlSection = ({ output }) => {
  const { type, id, value, fnId, isOutputBoolean, mappedOutputId } = output
  const dispatch = useDispatch()
  const isNativeOutputType = isNativeOutput(type)
  const showOutputEvaluation = useSelector(getShowOutputEvaluation, {
    fnId,
    id,
  })
  const { isSingleConditionOutput, isSixteenBitCan, disabled } = useSelector(
    getSingleConditionMetaData,
    { id, fnId }
  )
  const outputQuery = useSelector(getOutputQuery, { fnId })
  const noOutputQuery = isEmptyOrNil(outputQuery)
  const { queryType, uri, subType } = outputQuery || {}

  const showEvaluation = showOutputEvaluation && !isNativeOutputType
  const hasCarryOutputQuery = QUERY_BOX_TYPES.has(queryType)
  // input condition data selector
  const condition = useMakeSelector(makeConditionSelector, {
    queryUri: uri,
  })
  const { inputUri } = condition
  const currentSignal = useMakeSelector(makeSignalSelector, {
    signal: inputUri,
  })
  const isSwitchSignal = propOr([], 'switches', currentSignal).length > 0
  const hasNativeOutputWithSelectionVal =
    isNativeOutputType && !isNil(mappedOutputId)

  const getCurrentlySelected = () => {
    if (hasCarryOutputQuery || noOutputQuery) return CARRY
    return subType
  }

  const handleEvaluationChange = useCallback(
    (event) => {
      dispatch(setOutputField('value', event.target.value, id))
    },
    [dispatch, id]
  )

  const [selectedType, setSelectedType] = useState(getCurrentlySelected())
  // show selections for measure for AI, operator/polarity/value for DI (fixed ie signal and floating)

  const handleTypeChange = useCallback(
    (event) => {
      const { value: newType } = event.target

      setSelectedType(newType)

      // handle simple output queries
      if (newType !== CARRY) {
        const isCanInput = newType === CAN_INPUT
        const newQueryType = isCanInput ? CAN_INPUT : SIMPLE

        const defaultNumberOfBits = isCanInput
          ? getDefaultNumberOfBits({
              isSixteenBitCan,
              isNativeOutputType,
              isSingleConditionOutput,
              isOutputBoolean,
            })
          : null
        dispatch(
          switchInputType(uri, newType, { defaultNumberOfBits }, newQueryType)
        )
      }

      if (!noOutputQuery && newType === CARRY) {
        dispatch(deleteSingleCondition(prop('uri', outputQuery), fnId))
      } else if (noOutputQuery) {
        dispatch(
          addSingleCondition(
            newType,
            fnId,
            isSixteenBitCan,
            isNativeOutputType,
            isOutputBoolean
          )
        )
      }
    },
    [
      dispatch,
      setSelectedType,
      isSixteenBitCan,
      isSingleConditionOutput,
      isNativeOutputType,
      isOutputBoolean,
      outputQuery,
      noOutputQuery,
      fnId,
      uri,
    ]
  )

  useEffect(() => {
    if (!noOutputQuery && isSixteenBitCan && selectedType === DIGITAL_INPUT) {
      handleTypeChange({ target: { value: CARRY } })
    }
  }, [handleTypeChange, isSixteenBitCan, noOutputQuery, selectedType])

  // override native input selection value or subtype with switch input if it has switches
  const inputTypeSelection = isSwitchSignal ? SWITCH_INPUT : selectedType
  const showControlOutputWithInput =
    hasNativeOutputWithSelectionVal || isCanOutput(type) || isPinOutput(type)
  if (!showControlOutputWithInput) return null
  return (
    <StyledInputSection>
      <DrawerDivider />
      <div className="input-row" data-testid="control-output-with-header">
        <InputLabel
          label={strings.input_from_label}
          weight="bold"
          size="large"
        />
        <HoverableInfoIcon tooltip={strings.input_from_label_hint} />
      </div>
      <InputTypeEditor
        currentValue={inputTypeSelection}
        disabled={disabled}
        isNativeOutput={isNativeOutputType}
        onTypeChange={handleTypeChange}
      />
      <OutputControlSelection
        selectedType={selectedType}
        fnId={fnId}
        id={id}
        outputQueryUri={uri}
        isSingleConditionOutput={isSingleConditionOutput}
        isNativeOutput={isNativeOutputType}
      />
      {showEvaluation && (
        <Evaluation
          tooltip={strings.evaluation_output}
          onChange={(event) => handleEvaluationChange(event)}
          value={value}
          className="evaluation-form-field"
        />
      )}
    </StyledInputSection>
  )
}

OutputControlSection.propTypes = {
  output: PropTypes.shape({
    type: PropTypes.string,
    id: PropTypes.string,
    fnId: PropTypes.string,
    value: PropTypes.number,
    isOutputBoolean: PropTypes.bool,
    mappedOutputId: PropTypes.string,
  }).isRequired,
}

export default OutputControlSection
